import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Sheet, SheetContent } from './ui/sheet';
import { Drawer, DrawerContent } from './ui/drawer';
import api from '../lib/api';
import { Building2, X, Clock } from 'lucide-react';

const PIPELINE = [
  'Application Submitted',
  'Bank Assigned',
  'Sanctioned - Loan Approved',
  'Disbursed',
  'Commission Processing',
  'Commission Paid',
];
const TERMINAL = { commission_paid: true, commission_rejected: true };
const statusLabels = {
  application_submitted: 'Application Submitted',
  bank_assigned: 'Bank Assigned',
  sanctioned: 'Sanctioned - Loan Approved',
  sanctioned_pdd: 'Sanctioned - PDD',
  declined: 'Declined - Loan Rejected',
  on_hold: 'On Hold - Under Evaluation',
  action_required: 'Action Required - Upload Documents',
  disbursed: 'Disbursed',
  commission_rejected: 'Commission Rejected',
  commission_processing: 'Commission Processing',
  commission_paid: 'Commission Paid',
};
const statusCls = {
  application_submitted: 'bg-blue-100 text-blue-700 border border-blue-200',
  bank_assigned: 'bg-sky-100 text-sky-700 border border-sky-200',
  sanctioned: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  sanctioned_pdd: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  declined: 'bg-red-100 text-red-700 border border-red-200',
  on_hold: 'bg-amber-100 text-amber-800 border border-amber-200',
  action_required: 'bg-orange-100 text-orange-800 border border-orange-200',
  disbursed: 'bg-teal-100 text-teal-700 border border-teal-200',
  commission_rejected: 'bg-red-100 text-red-700 border border-red-200',
  commission_processing: 'bg-amber-100 text-amber-700 border border-amber-200',
  commission_paid: 'bg-green-100 text-green-800 border border-green-200',
};

const COMMISSION_PREVIEW_STATUSES = ['application_submitted', 'bank_assigned', 'sanctioned', 'sanctioned_pdd'];

const fmtCurrency = (amt) => amt ? `₹ ${Number(amt).toLocaleString('en-IN')}` : '₹ 0';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const leadShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customer_name: PropTypes.string,
  current_status: PropTypes.string,
  lead_reference: PropTypes.string,
  crm_lead_id: PropTypes.string,
  loan_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disbursement_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  commission_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  adjusted_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  commission_paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  product_name: PropTypes.string,
  created_at: PropTypes.string,
  customer_mobile: PropTypes.string,
  bank_name: PropTypes.string,
  notes: PropTypes.string,
  timeline: PropTypes.array,
});

const commissionPreviewShape = PropTypes.shape({
  commission_range: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    min_pct: PropTypes.number,
    max_pct: PropTypes.number,
  }),
  configs: PropTypes.array,
});

const timelineShape = PropTypes.arrayOf(PropTypes.shape({
  title: PropTypes.string,
  description: PropTypes.string,
  date: PropTypes.string,
}));

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 640);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function mapTimelineEntry(e, lead) {
  if (e.event_source === 'note') {
    const noteBy = e.event_data?.by || 'Unknown';
    const noteText = e.event_data?.note || '';
    return { 
      title: `Note by ${noteBy}`, 
      description: noteText || null, 
      date: fmtDate(e.created_at) 
    };
  }

  if (e.event_source === 'commission_adjustment') {
    const adjustedBy = e.event_data?.by || 'Admin';
    const originalAmt = e.event_data?.original_amount;
    const adjustedAmt = e.event_data?.adjusted_amount;
    const remarks = e.event_data?.remarks || '';
    let description = `Commission adjusted from ${fmtCurrency(originalAmt)} to ${fmtCurrency(adjustedAmt)}`;
    if (remarks) description += `\nRemarks: ${remarks}`;
    return {
      title: `Adjusted by ${adjustedBy}`,
      description: description,
      date: fmtDate(e.created_at)
    };
  }

  if (e.event_source === 'commission_rejection') {
    const rejectedBy = e.event_data?.by || 'Admin';
    const remarks = e.event_data?.remarks || '';
    return {
      title: 'Commission Rejected',
      description: remarks ? `Rejected by ${rejectedBy}. ${remarks}` : `Rejected by ${rejectedBy}`,
      date: fmtDate(e.created_at)
    };
  }

  const toLabel = statusLabels[e.to_status] || e.to_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const fromLabel = statusLabels[e.from_status] || e.from_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  let description = '';
  if (!e.from_status) {
    // Initial submission entry — prefer event_data.notes, fall back to lead.notes
    description = e.event_data?.notes || e.event_data?.remarks || lead.notes || '';
  } else {
    description = `Status moved from ${fromLabel} to ${toLabel}`;
  }
  if (e.event_source === 'crm_webhook' && e.from_status) description += ' (via Zoho CRM)';

  const remarks = e.event_data?.remarks || '';
  if (remarks && e.from_status) {
    // Only append remarks separately for status transitions (initial entry already has it above)
    description = description ? `${description}\nRemarks: ${remarks}` : `Remarks: ${remarks}`;
  }

  return { id: e.id, title: toLabel, description: description || null, date: fmtDate(e.created_at) };
}

function buildTimeline(lead) {
  if (!lead.timeline || lead.timeline.length === 0) {
    return [{ id: `new-${lead.id}`, title: 'New', description: lead.notes || null, date: fmtDate(lead.created_at) }];
  }
  const entries = lead.timeline.map(e => mapTimelineEntry(e, lead)).reverse();

  // Deduplicate entries - remove duplicates with same title, description, and date
  const seen = new Set();
  const dedupedEntries = entries.filter(e => {
    const key = `${e.title}|${e.description || ''}|${e.date}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Always ensure "Application Submitted" is at the top if not already present
  const hasApplicationSubmitted = dedupedEntries.some(e => e.title === 'Application Submitted');
  if (!hasApplicationSubmitted) {
    dedupedEntries.unshift({ title: 'Application Submitted', description: null, date: fmtDate(lead.created_at) });
  }
  return dedupedEntries;
}

function getStepState(i, pipelineIdx, isTerm, isRejected) {
  const idx = Math.max(0, pipelineIdx);
  
  // For commission_rejected: show up to Disbursed (index 3) as completed, rest as frozen
  if (isRejected) {
    const disbursedIdx = 3; // Index of 'Disbursed' in PIPELINE
    const isCompleted = i <= disbursedIdx;
    const isCurrent = false;
    const isUpcoming = i > disbursedIdx;
    const isFrozen = i > disbursedIdx;
    return { isCompleted, isCurrent, isUpcoming, isFrozen };
  }
  
  const isCompleted = isTerm ? i < PIPELINE.length : i < idx;
  const isCurrent = !isTerm && i === idx;
  const isUpcoming = !isTerm && i > idx;
  return { isCompleted, isCurrent, isUpcoming, isFrozen: false };
}

function stepDotClass(isCompleted, isCurrent, isTerm, isFrozen) {
  if (isFrozen) return 'w-[24px] h-[24px] bg-white border-2 border-[#d1d5db] opacity-50';
  const activeColor = isTerm ? 'bg-[#10b981]' : 'bg-[#2563eb]';
  if (isCompleted) return `w-[24px] h-[24px] ${activeColor}`;
  if (isCurrent) return `w-[24px] h-[24px] ${activeColor} ring-[3px] ${isTerm ? 'ring-emerald-100' : 'ring-blue-100'}`;
  return 'w-[24px] h-[24px] bg-white border-2 border-[#d1d5db]';
}

function stepDotClassDesktop(isCompleted, isCurrent, isTerm, isFrozen) {
  if (isFrozen) return 'w-[32px] h-[32px] bg-white border-2 border-[#d1d5db] opacity-50';
  const activeColor = isTerm ? 'bg-[#10b981]' : 'bg-[#2563eb]';
  if (isCompleted) return `w-[32px] h-[32px] ${activeColor}`;
  if (isCurrent) return `w-[32px] h-[32px] ${activeColor} ring-4 ${isTerm ? 'ring-emerald-100' : 'ring-blue-100'}`;
  return 'w-[32px] h-[32px] bg-white border-2 border-[#d1d5db]';
}

function lineColor(active, isTerm, isFrozen) {
  if (isFrozen) return 'bg-[#e5e7eb] opacity-50';
  if (!active) return 'bg-[#e5e7eb]';
  return isTerm ? 'bg-[#10b981]' : 'bg-[#2563eb]';
}

function CheckIcon() {
  return (
    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5 9-10"/>
    </svg>
  );
}

PipelineStep.propTypes = {
  step: PropTypes.string.isRequired,
  i: PropTypes.number.isRequired,
  pipelineIdx: PropTypes.number.isRequired,
  isTerm: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool,
  isRejectedPipeline: PropTypes.bool,
  isLastStep: PropTypes.bool,
  desktop: PropTypes.bool,
};
function PipelineStep({ step, i, pipelineIdx, isTerm, isRejected, isRejectedPipeline, isLastStep, desktop }) {
  const { isCompleted, isCurrent, isUpcoming, isFrozen } = getStepState(i, pipelineIdx, isTerm, isRejected);
  const isDisbursed = step === 'Disbursed';
  const isLastInRejectedPipeline = isRejectedPipeline && isDisbursed;
  
  const dotClass = desktop ? stepDotClassDesktop(isCompleted, isCurrent, isTerm, isFrozen) : stepDotClass(isCompleted, isCurrent, isTerm, isFrozen);
  const lineH = desktop ? 'h-[2.5px] top-[15px]' : 'h-[2px] top-[12px]';

  return (
    <div className={`flex-1 flex flex-col items-center relative ${isFrozen ? 'opacity-50' : ''}`}>
      {i > 0 && (
        <div className={`absolute right-1/2 w-full ${lineH} ${lineColor(isCompleted || isCurrent, isTerm, isFrozen)}`} />
      )}
      {!isLastStep && !isLastInRejectedPipeline && (
        <div className={`absolute left-1/2 w-full ${lineH} ${lineColor(isCompleted && !isCurrent, isTerm, isFrozen)}`} />
      )}
      {isLastInRejectedPipeline && (
        <div className={`absolute left-1/2 w-full ${lineH} bg-[#ef4444]`} />
      )}
      <div className={`relative z-10 flex items-center justify-center rounded-full ${desktop ? 'transition-all' : ''} ${dotClass}`}>
        {isCompleted && !isFrozen && <CheckIcon />}
        {isCurrent && <div className={`${desktop ? 'w-2.5 h-2.5' : 'w-2 h-2'} bg-white rounded-full`} />}
        {(isUpcoming || isFrozen) && <div className={`${desktop ? 'w-2.5 h-2.5' : 'w-2 h-2'} bg-[#d1d5db] rounded-full`} />}
      </div>
      <span className={`mt-1.5 ${desktop ? 'text-[9px] mt-2' : 'text-[7px]'} font-medium text-center leading-tight ${isCompleted || isCurrent ? 'text-[#111827]' : 'text-[#9ca3af]'} ${isFrozen ? 'opacity-50' : ''}`}>
        {step}
      </span>
    </div>
  );
}

// Red X step for Commission Rejected
RejectedStep.propTypes = {
  desktop: PropTypes.bool,
};
function RejectedStep({ desktop }) {
  const lineH = desktop ? 'h-[2.5px] top-[15px]' : 'h-[2px] top-[12px]';
  return (
    <div className="flex-1 flex flex-col items-center relative">
      <div className={`absolute right-1/2 w-full ${lineH} bg-[#ef4444]`} />
      <div className={`relative z-10 flex items-center justify-center rounded-full bg-[#ef4444] ${desktop ? 'w-[32px] h-[32px]' : 'w-[24px] h-[24px]'}`}>
        <svg className={`${desktop ? 'w-3.5 h-3.5' : 'w-3 h-3'} text-white`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12"/>
        </svg>
      </div>
      <span className={`mt-1.5 ${desktop ? 'text-[9px] mt-2' : 'text-[7px]'} font-medium text-center leading-tight text-[#ef4444]`}>
        Commission Rejected
      </span>
    </div>
  );
}

CommissionPreview.propTypes = {
  commissionLoading: PropTypes.bool.isRequired,
  commissionPreview: commissionPreviewShape,
  mobile: PropTypes.bool,
};
function CommissionPreview({ commissionLoading, commissionPreview, mobile }) {
  if (commissionLoading) {
    return mobile ? (
      <div className="bg-blue-50 border border-blue-200 rounded-[16px] p-4 animate-pulse">
        <div className="h-4 bg-blue-100 rounded w-40 mb-2" />
        <div className="h-5 bg-blue-100 rounded w-56" />
      </div>
    ) : (
      <div className="border border-gray-200 rounded-lg px-4 py-3 animate-pulse">
        <div className="h-3.5 bg-gray-100 rounded w-36 mb-2" />
        <div className="h-4 bg-gray-100 rounded w-48" />
      </div>
    );
  }

  const range = commissionPreview?.commission_range;
  if (!range || range.min <= 0) return null;

  const hasRange = range.min !== range.max;
  const hasPctRange = range.min_pct !== range.max_pct;

  return mobile ? (
    <div className="bg-blue-50 border border-blue-200 rounded-[16px] p-4">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-blue-600" />
        <p className="text-[11px] text-blue-700 font-semibold">Estimated Commission Range</p>
      </div>
      <p className="text-[15px] font-bold text-blue-800 tabular">
        {fmtCurrency(range.min)}{hasRange && <> — {fmtCurrency(range.max)}</>}
      </p>
      <p className="text-[10px] text-blue-600 font-medium mt-1">
        {range.min_pct}%{hasPctRange ? ` - ${range.max_pct}%` : ''}
      </p>
    </div>
  ) : (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg px-4 py-3">
      <div className="flex items-center gap-2 mb-1.5">
        <Building2 className="w-3.5 h-3.5 text-blue-600" />
        <p className="text-[10px] text-blue-700 font-semibold">Estimated Commission Range</p>
      </div>
      <p className="text-[15px] font-bold text-blue-800 tabular">
        {fmtCurrency(range.min)}{hasRange && <> — {fmtCurrency(range.max)}</>}
      </p>
      <p className="text-[10px] text-blue-600 font-medium mt-1">
        {range.min_pct}%{hasPctRange ? ` - ${range.max_pct}%` : ''}
      </p>
    </div>
  );
}

PipelineProgress.propTypes = {
  pipelineIdx: PropTypes.number.isRequired,
  isTerm: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool,
  stageNum: PropTypes.number.isRequired,
  stagePercent: PropTypes.number.isRequired,
  desktop: PropTypes.bool,
};
function PipelineProgress({ pipelineIdx, isTerm, isRejected, stageNum, stagePercent, desktop }) {
  return (
    <div className={`bg-[#f8fafc] ${desktop ? 'rounded-[12px] lg:rounded-[16px]' : 'rounded-[12px]'} p-3 border border-[#edf2f7]`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[1px] font-bold text-[#64748b]">Loan Progress</p>
        {!isRejected && (
          <p className={`text-[${desktop ? '11' : '10'}px] text-[#6b7280]`}>
            Stage <span className="font-semibold text-[#374151]">{stageNum} of {PIPELINE.length}</span> · {stagePercent}%{desktop ? ' complete' : ''}
          </p>
        )}
      </div>
      <div className="flex items-start pt-1 pb-1">
        {isRejected ? (
          // When rejected, show only first 4 steps + Commission Rejected
          <>
            {PIPELINE.slice(0, 4).map((step, i) => (
              <PipelineStep key={step} step={step} i={i} pipelineIdx={pipelineIdx} isTerm={isTerm} isRejected={isRejected} desktop={desktop} isRejectedPipeline />
            ))}
            <RejectedStep desktop={desktop} />
          </>
        ) : (
          PIPELINE.map((step, i) => (
            <PipelineStep key={step} step={step} i={i} pipelineIdx={pipelineIdx} isTerm={isTerm} isRejected={isRejected} desktop={desktop} isLastStep={i === PIPELINE.length - 1} />
          ))
        )}
      </div>
    </div>
  );
}

MobileDrawerContent.propTypes = {
  lead: leadShape.isRequired,
  label: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  commissionPreview: commissionPreviewShape,
  commissionLoading: PropTypes.bool.isRequired,
  pipelineIdx: PropTypes.number.isRequired,
  isTerm: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool,
  stageNum: PropTypes.number.isRequired,
  stagePercent: PropTypes.number.isRequired,
  timeline: timelineShape.isRequired,
};
function MobileDrawerContent({ lead, label, initials, commissionPreview, commissionLoading, pipelineIdx, isTerm, isRejected, stageNum, stagePercent, timeline }) {
  return (
    <div className="overflow-y-auto pb-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      <div className="px-3 pt-3 pb-1.5 mb-3">
        <div className="flex items-start gap-4">
          <div className="w-[44px] h-[44px] rounded-[12px] bg-[#2563eb] text-white flex items-center justify-center text-[15px] font-bold shrink-0">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h3 className="text-[17px] font-bold text-[#1e293b] truncate">{lead.customer_name}</h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${statusCls[lead.current_status] || 'bg-gray-100 text-gray-600 border border-gray-200'}`}>{label}</span>
            </div>
            <p className="text-[12px] text-[#64748b] mt-1">{lead.crm_lead_id || lead.lead_reference}</p>
          </div>
        </div>
      </div>
      <div className="px-3 pb-3 space-y-2.5">
        <div className="bg-[#f8fafc] rounded-[12px] p-3 border border-[#edf2f7]">
          <p className="text-[10px] uppercase tracking-[1px] font-bold text-[#64748b] mb-3">Loan Summary</p>
          <div className="space-y-2">
            <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Loan Amount</p><p className="text-[15px] font-bold text-[#1e293b] tabular">{fmtCurrency(lead.loan_amount)}</p></div>
            <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Disbursed Amount</p><p className="text-[15px] font-bold text-emerald-700 tabular">{lead.disbursement_amount ? fmtCurrency(lead.disbursement_amount) : '—'}</p></div>
            <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Product Type</p><p className="text-[13px] font-semibold text-[#1e293b]">{lead.product_name || '—'}</p></div>
            <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Applied On</p><p className="text-[13px] font-semibold text-[#1e293b]">{fmtDate(lead.created_at)}</p></div>
          </div>
        </div>

        {COMMISSION_PREVIEW_STATUSES.includes(lead.current_status) && (
          <CommissionPreview commissionLoading={commissionLoading} commissionPreview={commissionPreview} mobile />
        )}

        <PipelineProgress pipelineIdx={pipelineIdx} isTerm={isTerm} isRejected={isRejected} stageNum={stageNum} stagePercent={stagePercent} desktop={false} />

        <div className="bg-[#f8fafc] rounded-[12px] p-3 border border-[#edf2f7]">
          <p className="text-[10px] uppercase tracking-[1px] font-bold text-[#64748b] mb-4">Activity Timeline</p>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div key={item.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#2563eb] shrink-0 mt-1" />
                  {i < timeline.length - 1 && <div className="w-[1.5px] flex-1 bg-[#e5e7eb] my-1" />}
                </div>
                <div className="flex-1 pb-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[12px] font-medium text-[#1e293b]">{item.title}</span>
                    <span className="text-[10px] text-[#94a3b8] shrink-0">{item.date}</span>
                  </div>
                  {item.description && <p className="text-[11px] text-[#6b7280] mt-0.5 leading-relaxed whitespace-pre-line">{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

DesktopSheetContent.propTypes = {
  lead: leadShape.isRequired,
  label: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  commissionPreview: commissionPreviewShape,
  commissionLoading: PropTypes.bool.isRequired,
  pipelineIdx: PropTypes.number.isRequired,
  isTerm: PropTypes.bool.isRequired,
  isRejected: PropTypes.bool,
  stageNum: PropTypes.number.isRequired,
  stagePercent: PropTypes.number.isRequired,
  timeline: timelineShape.isRequired,
};
function DesktopSheetContent({ lead, label, initials, onClose, commissionPreview, commissionLoading, pipelineIdx, isTerm, isRejected, stageNum, stagePercent, timeline }) {
  return (
    <>
      <div className="flex items-center gap-3 px-5 pt-5 pb-4 shrink-0">
        <div className="w-10 h-10 rounded-full bg-[#2563eb] text-white flex items-center justify-center text-[13px] font-bold shrink-0">{initials}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-bold text-[#1e293b] truncate">{lead.customer_name}</h2>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-semibold shrink-0 ${statusCls[lead.current_status] || 'bg-gray-100 text-gray-600 border border-gray-200'}`}>{label}</span>
          </div>
          <p className="text-[11px] text-[#64748b] mt-0.5 truncate">
            {lead.crm_lead_id || lead.lead_reference} · {lead.product_name}
            {lead.customer_mobile && <> · <span className="text-[#374151]">{lead.customer_mobile}</span></>}
          </p>
        </div>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 transition-colors shrink-0" aria-label="Close">
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <LoanSummaryDesktop lead={lead} />

        {COMMISSION_PREVIEW_STATUSES.includes(lead.current_status) && (
          <CommissionPreview commissionLoading={commissionLoading} commissionPreview={commissionPreview} mobile={false} />
        )}

        <PipelineProgress pipelineIdx={pipelineIdx} isTerm={isTerm} isRejected={isRejected} stageNum={stageNum} stagePercent={stagePercent} desktop />

        <div>
          <div className="flex items-center gap-1.5 mb-3">
            <Clock className="w-3.5 h-3.5 text-[#6b7280]" />
            <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#374151]">Activity Timeline</h3>
          </div>
          <div className="space-y-0">
            {timeline.map((item, i) => (
              <div key={item.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-3 h-3 rounded-full shrink-0 mt-1 bg-[#2563eb]" />
                  {i < timeline.length - 1 && <div className="w-[1.5px] flex-1 bg-[#e5e7eb] my-1" />}
                </div>
                <div className="flex-1 pb-4 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-[12px] font-semibold text-[#111827]">{item.title}</p>
                    <span className="text-[9px] text-[#6b7280] shrink-0 mt-0.5 flex items-center gap-1">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      {item.date}
                    </span>
                  </div>
                  {item.description && <p className="text-[10px] text-[#6b7280] mt-0.5 leading-relaxed whitespace-pre-line">{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

LoanSummaryDesktop.propTypes = {
  lead: leadShape.isRequired,
};
function LoanSummaryDesktop({ lead }) {
  // Check if commission is paid (show this with priority)
  const showCommissionPaid = lead.current_status === 'commission_paid' && lead.commission_paid;
  // Check if we should show Final Commission (only if NOT paid yet)
  const showFinalCommission = !showCommissionPaid && lead.bank_name && (lead.adjusted_amount ?? lead.commission_amount);
  
  return (
    <div className="bg-[#f8fafc] rounded-[12px] lg:rounded-[16px] p-3 border border-[#edf2f7]">
      <p className="text-[10px] uppercase tracking-[1px] font-bold text-[#64748b] mb-3">Loan Summary</p>
      {/* Mobile Layout */}
      <div className="sm:hidden space-y-2">
        <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Loan Amount</p><p className="text-[15px] font-bold text-[#1e293b] tabular">{fmtCurrency(lead.loan_amount)}</p></div>
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Product Type</p><p className="text-[13px] font-semibold text-[#1e293b]">{lead.product_name || '—'}</p></div>
          <div className="text-right"><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Applied On</p><p className="text-[13px] font-semibold text-[#1e293b]">{fmtDate(lead.created_at)}</p></div>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Disbursed Amount</p><p className="text-[15px] font-bold text-emerald-700 tabular">{lead.disbursement_amount ? fmtCurrency(lead.disbursement_amount) : '—'}</p></div>
          {lead.bank_name && <div className="text-right"><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Bank Assigned</p><p className="text-[13px] font-semibold text-[#1e293b]">{lead.bank_name}</p></div>}
        </div>
        {(showFinalCommission || showCommissionPaid) && (
          <div className="flex items-start justify-between gap-3">
            {showFinalCommission && <div><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Final Commission</p><p className="text-[13px] font-semibold text-amber-600 tabular">{fmtCurrency(lead.adjusted_amount ?? lead.commission_amount)}</p></div>}
            {showCommissionPaid && <div className="text-right"><p className="text-[10px] text-[#94a3b8] font-medium mb-0.5">Commission Paid</p><p className="text-[13px] font-semibold text-green-600 tabular">{fmtCurrency(lead.commission_paid)}</p></div>}
          </div>
        )}
      </div>
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        {/* Row 1: Loan Amount | Product Type | Applied On */}
        <div className="grid grid-cols-3 gap-2">
          <div><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Loan Amount</p><p className="text-[15px] font-bold text-[#1e293b] tabular">{fmtCurrency(lead.loan_amount)}</p></div>
          <div className="text-center"><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Product Type</p><p className="text-[13px] font-semibold text-[#1e293b]">{lead.product_name || '—'}</p></div>
          <div className="text-right"><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Applied On</p><p className="text-[13px] font-semibold text-[#1e293b]">{fmtDate(lead.created_at)}</p></div>
        </div>
        {/* Row 2: Disbursed Amount | Bank Assigned | Final Commission | Commission Paid */}
        <div className="flex items-start justify-between gap-4 pt-2 mt-1 border-t border-[#edf2f7]">
          <div><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Disbursed Amount</p><p className="text-[15px] font-bold text-emerald-700 tabular">{lead.disbursement_amount ? fmtCurrency(lead.disbursement_amount) : '—'}</p></div>
          {lead.bank_name && <div><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Bank Assigned</p><p className="text-[13px] font-semibold text-[#1e293b]">{lead.bank_name}</p></div>}
          {showFinalCommission && <div><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Final Commission</p><p className="text-[13px] font-semibold text-amber-600 tabular">{fmtCurrency(lead.adjusted_amount ?? lead.commission_amount)}</p></div>}
          {showCommissionPaid && <div><p className="text-[11px] text-[#94a3b8] font-medium mb-0.5">Commission Paid</p><p className="text-[13px] font-semibold text-green-600 tabular">{fmtCurrency(lead.commission_paid)}</p></div>}
        </div>
      </div>
    </div>
  );
}

export default function LeadDetailModal({ lead, onClose }) {
  const isDesktop = useIsDesktop();
  const [commissionPreview, setCommissionPreview] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);

  useEffect(() => {
    if (!lead?.id || lead.current_status === 'commission_paid') {
      setCommissionPreview(null);
      return;
    }
    setCommissionLoading(true);
    api.get(`/api/admin/leads/${lead.id}/commission-preview`)
      .then(res => {
        const data = res.data;
        if (data.configs?.length > 0) {
          setCommissionPreview({
            configs: data.configs.map(c => ({ bank_name: c.bank_name, commission_pct: c.commission_pct || 0, estimated_commission: c.estimated_commission })),
            commission_range: data.commission_range,
          });
        } else {
          setCommissionPreview(null);
        }
      })
      .catch(() => setCommissionPreview(null))
      .finally(() => setCommissionLoading(false));
  }, [lead]);

  if (!lead) return null;

  const label = statusLabels[lead.current_status] || lead.current_status;
  const isTerm = !!TERMINAL[lead.current_status];
  const isRejected = lead.current_status === 'commission_rejected';
  // Side evaluation statuses stay highlighted at Bank Assigned (not linear pipeline steps)
  const evaluationPipelineIdx = { on_hold: 1, action_required: 1, declined: 1, sanctioned_pdd: 2 };
  const pipelineIdx = Object.prototype.hasOwnProperty.call(evaluationPipelineIdx, lead.current_status)
    ? evaluationPipelineIdx[lead.current_status]
    : (isRejected ? 3 : PIPELINE.findIndex(s => s === label));
  const stageNum = isRejected ? 4 : Math.max(0, pipelineIdx) + 1;
  const stagePercent = Math.round((stageNum / PIPELINE.length) * 100);
  const initials = lead.customer_name
    ? lead.customer_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'NA';
  const timeline = buildTimeline(lead);

  const sharedProps = { lead, label, initials, commissionPreview, commissionLoading, pipelineIdx, isTerm, isRejected, stageNum, stagePercent, timeline };

  if (!isDesktop) {
    return (
      <Drawer open={!!lead} onOpenChange={open => { if (!open) onClose(); }}>
        <DrawerContent className="h-[75vh] border-0 outline-none">
          <MobileDrawerContent {...sharedProps} />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={!!lead} onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] p-0 bg-white border-l border-gray-200 flex flex-col h-full [&>button.absolute]:hidden">
        <DesktopSheetContent {...sharedProps} onClose={onClose} />
      </SheetContent>
    </Sheet>
  );
}

LeadDetailModal.propTypes = {
  lead: leadShape,
  onClose: PropTypes.func.isRequired,
};
